/**
 * The room activity read's refresh rules (`GET /api/rooms/{roomId}/activity`, performance rule 12): its data
 * class is *changes with time*. The Overview's player cards are the room's month so far and its dormancy, read
 * at the room's gaming day, and the boards-behind card moves on writes, which this app invalidates at once.
 *
 * The rule's "keyed by the room's day" is met here without the day in the key, deliberately. The read runs
 * beside the room summary rather than after it — measured in rank 4's Phase 0, it already ends last — and the
 * day is what the summary answers, so a key carrying it would hold the activity read until the summary had
 * returned. What the key would buy is that no answer about yesterday is served as today's, and two rules give
 * that instead: the figures name the day they were computed at (`today`), and they are stale the moment the
 * summary names another day; and they are never fresh past the room's next boundary, when they are asked again.
 *
 * Pure, and importing only `summary.ts`, so `node --test` drives it through a real TanStack observer on a fake
 * clock (`refresh.test.js`).
 */
import { BOUNDARY_MARGIN_MS, untilNextDay } from '../summary.ts';

/**
 * How long the figures are fresh: a minute. They are already up to about fifteen seconds behind (the
 * engagement flush, then the relay), and a minute more is still the month so far.
 */
export const ACTIVITY_FRESH_MS = 60_000;

interface RoomDay {
  day: string;
  nextDayStartsAt: string;
}

interface Observed {
  state: { data?: { today: string } | undefined };
}

/**
 * The query options the activity hook spreads in, from what the room summary last said about the room's day
 * (undefined until it has answered).
 *
 * - **staleTime**: a minute, or less when the room's day ends sooner; none at all once the summary names a day
 *   other than the one the figures were computed at.
 * - **refetchInterval**: the time to the room's next day, so an Overview left open overnight does not keep
 *   yesterday's month so far — this app does not refetch on focus. Nothing until the room's day is known.
 * - **refetchIntervalInBackground**: as the summary's: one small request a day.
 */
export function activityRefresh(roomDay: RoomDay | undefined) {
  return {
    staleTime: (query: Observed): number => {
      const today = query.state.data?.today;
      if (roomDay && today !== undefined && today !== roomDay.day) return 0;
      const left = untilNextDay(roomDay);
      return left === null ? ACTIVITY_FRESH_MS : Math.max(0, Math.min(ACTIVITY_FRESH_MS, left));
    },
    refetchInterval: (): number | false => {
      const left = untilNextDay(roomDay);
      return left === null ? false : Math.max(left, 0) + BOUNDARY_MARGIN_MS;
    },
    refetchIntervalInBackground: true,
  };
}
